import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProcessDataRequest, ProcessDataResponse, ProcessingStatus, ProcessingHistoryItem } from '../../types';
import { dataProcessingApi } from '../../services/dataProcessingApi';

interface DataProcessingState {
  isProcessing: boolean;
  status: ProcessingStatus | null;
  history: ProcessingHistoryItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: DataProcessingState = {
  isProcessing: false,
  status: null,
  history: [],
  isLoading: false,
  error: null,
};

export const processData = createAsyncThunk(
  'dataProcessing/processData',
  async (request: ProcessDataRequest, { rejectWithValue }) => {
    try {
      const response = await dataProcessingApi.processData(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to process data');
    }
  }
);

export const getProcessingStatus = createAsyncThunk(
  'dataProcessing/getProcessingStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dataProcessingApi.getProcessingStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get processing status');
    }
  }
);

export const getProcessingHistory = createAsyncThunk(
  'dataProcessing/getProcessingHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dataProcessingApi.getProcessingHistory();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get processing history');
    }
  }
);

const dataProcessingSlice = createSlice({
  name: 'dataProcessing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setProcessingStatus: (state, action: PayloadAction<ProcessingStatus>) => {
      state.status = action.payload;
      state.isProcessing = action.payload.is_processing;
    },
    clearProcessingStatus: (state) => {
      state.status = null;
      state.isProcessing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Process data
      .addCase(processData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isProcessing = true;
      })
      .addCase(processData.fulfilled, (state, action: PayloadAction<ProcessDataResponse>) => {
        state.isLoading = false;
        state.isProcessing = false;
        // Update status to completed
        if (state.status) {
          state.status.is_processing = false;
          state.status.progress = 100;
        }
      })
      .addCase(processData.rejected, (state, action) => {
        state.isLoading = false;
        state.isProcessing = false;
        state.error = action.payload as string;
        // Update status to failed
        if (state.status) {
          state.status.is_processing = false;
        }
      })
      // Get processing status
      .addCase(getProcessingStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProcessingStatus.fulfilled, (state, action: PayloadAction<ProcessingStatus>) => {
        state.isLoading = false;
        state.status = action.payload;
        state.isProcessing = action.payload.is_processing;
      })
      .addCase(getProcessingStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get processing history
      .addCase(getProcessingHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProcessingHistory.fulfilled, (state, action: PayloadAction<ProcessingHistoryItem[]>) => {
        state.isLoading = false;
        state.history = action.payload;
      })
      .addCase(getProcessingHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setProcessingStatus, clearProcessingStatus } = dataProcessingSlice.actions;
export default dataProcessingSlice.reducer;
